import { createStyles, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';

import { FeedAnswerFragment, FeedQuestionDetailsFragment, QuestionStatus } from '../../../../graphql/gen/types';
import { colorPalette, spacings } from '../../../../theme';
import { AnswerView } from './Answer';
import { AnswerLinkPreview } from './AnswerLinkPreview';

type Props = {
  className?: string;
  question: FeedQuestionDetailsFragment;
  answer?: FeedAnswerFragment;
  selected: boolean;
  showAllLines: boolean;
  headerView?: React.ReactNode;
  footerView?: React.ReactNode;
};

export const AnswerViewContent = React.memo(function AnswerViewContent(props: Props): React.ReactElement | null {
  const { className, question, answer, showAllLines, footerView, headerView } = props;
  const classes = useStyles();

  if (!answer) {
    return null;
  }

  const isAcceptedAnswer = answer.id === question.acceptedAnswer?.id;

  return (
    <div
      className={classNames(
        classes.root,
        isAcceptedAnswer && question.status === QuestionStatus.Published && classes.rootPublished,
        !isAcceptedAnswer && classes.rootNotAccepted,
        className
      )}
    >
      {headerView}
      <AnswerView answer={answer} showAllLines={showAllLines} />
      {!showAllLines && !!answer?.primaryLink && (
        <AnswerLinkPreview
          stopClickPropagation
          href={answer.primaryLink.id}
          data={{ state: 'done', details: answer.primaryLink.details }}
        />
      )}
      {footerView}
    </div>
  );
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colorPalette.purpleGray._50,
      marginLeft: spacings.normal,
      marginRight: spacings.normal,
      marginBottom: spacings.normal,
      borderRadius: 4,
      border: `1px solid ${colorPalette.purpleGray._100}`,
    },
    rootPublished: {
      borderColor: '#385FEA',
      backgroundColor: '#EDF1FF',
    },
    rootNotAccepted: {
      borderColor: '#DFDFE8',
      backgroundColor: '#fff',
    },
    content: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    answerRequiredContainer: {
      display: 'flex',
      marginLeft: spacings.xlarge,
    },
    answerRequired: {
      display: 'flex',
      color: colorPalette.primaryColorBlue._500,
      textDecorationLine: 'underline',
      cursor: 'pointer',
    },
  })
);
